import React, { useMemo, useState } from "react";
import { Auth } from "aws-amplify";
// ANT DESIGN COMPONENTS
import { Modal, Button, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
import { attemptsMade } from "../../redux/retryingBaseQuery";
import { isConnectionFailure, probeServerReachability } from "../loginOffline/connectionDiagnosis";
import { downloadLoginLogBundle, readBrowserEnvironment } from "../loginOffline/logBundle";
import {
  CURRENT_USER_FAILURE_TEXT,
  type CurrentUserFailure,
  describeCurrentUserFailure,
} from "./currentUserFailure";

const DEFAULT_APP_VARIANT = "v1";
const API_BASE_URL_KEY = "REACT_APP_LIFT_NET_CLOUD_API_ONLINE";

const currentUserUrl = (): string =>
  `${(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "").replace(/\/+$/, "")}/users/me`;

type UserNotFoundProps = {
  // Why /users/me ended without a user; absent means it answered without one.
  failure?: CurrentUserFailure;
};

/**
 * Shown when /users/me ends without a user. One plain message for the cause,
 * the reference to quote, and "Get log" for the technical detail support needs.
 */
function UserNotFound({ failure = undefined }: UserNotFoundProps) {
  const [t] = useTranslation("global");
  const { userLogged } = useSelector((state: any) => state.home);
  // Kept for the life of the modal so the reference on screen and in the log stay the same.
  const fallback = useMemo(() => describeCurrentUserFailure(undefined), []);
  const shown = failure ?? fallback;
  const [isPreparingLog, setIsPreparingLog] = useState(false);
  const text = CURRENT_USER_FAILURE_TEXT[shown.cause];

  const signOut = async () => {
    await Auth.signOut();
    window.location.href = `${process.env.REACT_APP_LOGIN_ROUTE}`;
  };

  const onClickGetLog = async () => {
    setIsPreparingLog(true);
    try {
      const requestUrl = currentUserUrl();
      // Checked now, when support asks for it, not on every failed request.
      const probe = isConnectionFailure(shown.error)
        ? await probeServerReachability(requestUrl, window.location.origin)
        : undefined;
      await downloadLoginLogBundle({
        title: "LiftNet sign-in log (loading your account)",
        generatedAt: new Date(),
        failedAt: shown.failedAt,
        appVariant: process.env.REACT_APP_VERSION || DEFAULT_APP_VARIANT,
        flow: `Load the signed-in account (${shown.cause})`,
        method: "GET",
        requestUrl,
        configKey: API_BASE_URL_KEY,
        attempts: attemptsMade(shown.error) ?? 1,
        error: shown.error ?? null,
        probe,
        environment: readBrowserEnvironment(),
        shownToUser: t(text.title),
        reference: shown.reference,
      });
    } catch {
      message.error(t("authentication.getLogFailed"));
    } finally {
      setIsPreparingLog(false);
    }
  };

  return (
    <Modal
      closable={false}
      open={!userLogged.acceptedTerms}
      width="50%"
      footer={
        <div className="generalStyles__flexEnd mt-1">
          <Button
            type="default"
            className="buttonStyle__2"
            loading={isPreparingLog}
            onClick={onClickGetLog}
          >
            {t("authentication.getLog")}
          </Button>
          <Button type="default" className="buttonStyle__2 ml-2" onClick={signOut}>
            {t("header.signOut")}
          </Button>
        </div>
      }
    >
      <div style={{ minHeight: "20vh", padding: "15px" }}>
        <div className="generalStyles__textJustify">
          <h2>{t(text.title)}</h2>
          <p>{t(text.hint)}</p>
          <p>{t("authentication.supportReference", { reference: shown.reference })}</p>
        </div>
      </div>
    </Modal>
  );
}

export default UserNotFound;
